/**
 * Glow Page Utility Functions
 * Helper functions for glow page display logic
 */

import type { Glow } from '../types/glow.types';

export enum GlowStatus {
  LIVE = 'live',
  SCHEDULED = 'scheduled',
  SUGGESTED = 'suggested',
  DRAFT = 'draft',
  ARCHIVED = 'archived',
  ENDED = 'ended',
}

/**
 * Compute display status based on glow data and current time
 */
export function computeDisplayStatus(
  glow: Glow,
  isMyGlow: boolean = false,
  currentTime: Date = new Date(),
): GlowStatus {
  // Handle DRAFT and ARCHIVED - always show as-is
  if (glow.status) {
    const backendStatus = glow.status.toUpperCase();
    if (backendStatus === 'DRAFT') return GlowStatus.DRAFT;
    if (backendStatus === 'ARCHIVED') return GlowStatus.ARCHIVED;
  }

  // For ACTIVE status, determine display status based on time
  try {
    if (glow.glowStartDate && glow.glowEndDate) {
      const startDate = new Date(glow.glowStartDate);
      const endDate = new Date(glow.glowEndDate);

      // If now is between start and end, it's Live
      if (currentTime > startDate && currentTime < endDate) {
        return GlowStatus.LIVE;
      }

      // Event hasn't started yet
      if (currentTime < startDate) {
        return isMyGlow ? GlowStatus.SCHEDULED : GlowStatus.SUGGESTED;
      }

      // Event has ended
      if (currentTime > endDate) {
        return GlowStatus.ENDED;
      }
    }
  } catch (e) {
    // If date parsing fails, treat as live
    console.error('Error parsing glow dates:', e);
  }

  return GlowStatus.LIVE;
}

/**
 * Get organization name from glow data
 */
export function getOrganizationName(glow: Glow): string {
  if (glow.user) {
    return `${glow.user.firstname || ''} ${glow.user.lastname || ''}`.trim();
  }
  if (glow.organization) {
    return glow.organization.name;
  }
  return '';
}

/**
 * Get organization avatar URL
 */
export function getOrganizationAvatar(glow: Glow): string | null {
  if (glow.user?.avatar) {
    return glow.user.avatar;
  }
  if (glow.organization?.avatar) {
    return glow.organization.avatar;
  }
  return null;
}

/**
 * Get display description (fallback to organization name if no description)
 */
export function getDisplayDescription(glow: Glow): string {
  const orgName = getOrganizationName(glow);

  if (glow.description && glow.description.trim() !== '') {
    return glow.description;
  }

  return `Check out what ${orgName} is up to!`;
}

/**
 * Compute online count (totalViews + 1 for current viewer)
 */
export function getOnlineCount(glow: Glow): number {
  return (glow.totalViews || 0) + 1;
}

/**
 * Get display raised amount (default to 0 if null)
 */
export function getDisplayRaisedAmount(glow: Glow): number {
  return glow.amountRaised || 0;
}

/**
 * Calculate seconds remaining until a date
 */
export function getSecondsUntil(targetDate: string): number {
  try {
    const target = new Date(targetDate);
    const now = new Date();
    const diff = target.getTime() - now.getTime();
    return Math.max(0, Math.floor(diff / 1000));
  } catch (e) {
    return 0;
  }
}

/**
 * Check if countdown overlay should be shown (glow starts within 30 minutes)
 */
export function shouldShowCountdown(glow: Glow, displayStatus: GlowStatus): boolean {
  if (displayStatus !== GlowStatus.SCHEDULED && displayStatus !== GlowStatus.SUGGESTED) {
    return false;
  }

  const secondsUntilStart = getSecondsUntil(glow.glowStartDate);
  const thirtyMinutes = 30 * 60; // 30 minutes in seconds

  return secondsUntilStart > 0 && secondsUntilStart <= thirtyMinutes;
}

/**
 * Format countdown time
 * Returns either "X days" or "HH:MM:SS"
 */
export function formatCountdownTime(seconds: number): { text: string; label: string } {
  const days = Math.floor(seconds / 86400);

  if (days > 0) {
    return {
      text: days.toString(),
      label: days === 1 ? 'day' : 'days',
    };
  }

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  return {
    text: `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`,
    label: '',
  };
}

/**
 * Format end countdown badge text
 */
export function formatEndCountdownBadge(glowEndDate: string): string {
  const seconds = getSecondsUntil(glowEndDate);

  if (seconds <= 0) {
    return 'Ended';
  }

  const days = Math.floor(seconds / 86400);

  if (days > 0) {
    return `Ends in ${days} ${days === 1 ? 'day' : 'days'}`;
  }

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hours > 0) {
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  }

  return `${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
}

/**
 * Get initials from name
 */
export function getInitials(name: string): string {
  const words = name.trim().split(/\s+/);
  if (words.length >= 2) {
    return (words[0][0] + words[1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
}

/**
 * Get button text based on sticker campaign type
 */
export function getCtaButtonText(
  campaignType: string | undefined | null,
): string {
  if (!campaignType) {
    return 'Purchase';
  }

  const type = campaignType.toLowerCase();

  if (type === 'sweepstakes') {
    return 'Enter to Win';
  }

  if (type === 'ticket') {
    return 'Register';
  }

  if (type === 'campaign') {
    return 'Donate';
  }

  // Default for regular stickers
  return 'Purchase';
}
